// MARK:- OS 2.0 Collection sort dropdown — Section Rendering API repaint.
//
// File naming: standalone module in this theme uses the `component-` file-name
// prefix so all standalone components sort together in `assets/` and grep nicely.
//
// Architecture: hand-edited asset, NOT a webpack/gulp build output. Loaded as an
// ES module from sections/collection-bar.liquid:
//
//   <script src="{{ 'component-collection-sort.js' | asset_url }}" type="module"></script>
//
// `type="module"` deduplicates by URL so multiple instances on the page only
// fetch + execute it once.
//
// Markup contract (set in sections/collection-bar.liquid):
//   <as-collection-sort>
//     <aside class="collection-bar" data-as-collection-bar>
//       ... snippets/sort-by-dropdown.liquid renders inputs[name="sort-by"] ...
//       <span class="small" data-as-collection-count>(N)</span>
//     </aside>
//   </as-collection-sort>
//
// Flow on sort change (radio click in the dropdown content):
// 1. Compute the new URL: location.pathname + ?sort_by=<value>, preserving any
//    other query params (e.g. ?filter.v.option.color=red later when filters land).
// 2. history.pushState to the new URL — back/forward works naturally.
// 3. Update the layout-level wrapper's data-as-collection-state attribute to
//    'filtered' (or 'curated' if user reset to default sort). Editorial sections
//    are hidden by CSS via the wrapper flip; inline <style> rules emitted by
//    each editorial section (scoped to [data-as-collection-state="curated"])
//    go inert, so claimed products in the grid become visible.
// 4. Update the bar's visible label inline. The bar is NOT swapped — see comment
//    on repaintForSort() for why.
// 5. fetch('<url>?sections=<grid-id>') — Section Rendering API returns
//    `{ <grid-id>: "<html>" }`. Replace the grid's section wrapper with the
//    fresh HTML. Products come back in the requested sort order.
// 6. On popstate (back/forward), re-read URL and repaint the same way, also
//    syncing the radio's `checked` state since no click happened.
//
// MARK:- Mobile-only fade: facets are position:fixed bottom:0 on ≤960px and overlap the footer-group at end-of-grid. IntersectionObserver on the footer-group wrapper toggles `.is-hidden` (translateY(100%) + opacity 0; transition already in component-collection-bar.css). matchMedia gates the observer so desktop never carries the class.

function buildUrl(sortValue) {
  const url = new URL(window.location.href);
  if (sortValue) {
    url.searchParams.set('sort_by', sortValue);
  } else {
    url.searchParams.delete('sort_by');
  }
  return url;
}

function getSectionIdFromShopifySection(shopifySectionEl) {
  // Shopify-section IDs look like "template--xxxx__bar" (JSON template) or
  // "shopify-section-template--...". The numeric/uuid suffix is the section's
  // settings key in the JSON template — the bit we pass to ?sections=.
  const id = shopifySectionEl?.id || '';
  if (id.startsWith('shopify-section-')) {
    return id.replace('shopify-section-', '');
  }
  return null;
}

async function repaintForSort(sortValue) {
  // MARK:- Only the grid is refreshed via Section Rendering API. The bar is NOT
  // replaced because the legacy `src/js/theme.js` binds `.dropdown__toggle` clicks
  // directly at DOMContentLoaded (not delegated) — replacing the bar's section
  // wrapper would orphan that binding and the dropdown would stop opening. The
  // bar's only sort-dynamic pieces are the visible label text and the checked
  // radio; both are updated inline in updateBarFromSort() / onChange().
  const targets = [];
  document.querySelectorAll('[data-as-collection-product-grid]').forEach((grid) => {
    const wrapper = grid.closest('.shopify-section');
    const id = getSectionIdFromShopifySection(wrapper);
    if (id) targets.push({ id, el: wrapper });
  });

  if (!targets.length) return;

  const url = buildUrl(sortValue);
  url.searchParams.set('sections', targets.map((t) => t.id).join(','));

  // MARK:- No Accept header — `Accept: application/json` makes Shopify route this to the legacy collection JSON endpoint (returns `{collection: {...metadata}}`) instead of the Section Rendering API. The plain GET with `?sections=` in the URL is what the SRA expects; it returns `{<section-id>: "<html>"}` automatically.
  let response;
  try {
    response = await fetch(url.toString());
  } catch (err) {
    return;
  }
  if (!response.ok) return;

  const data = await response.json();
  targets.forEach(({ id, el }) => {
    const html = data[id];
    if (typeof html !== 'string' || !el) return;
    // Section Rendering API returns the section content wrapped in its own
    // shopify-section div — replace the existing wrapper's outer HTML with
    // the fresh markup so attributes / classes update too.
    const tmp = document.createElement('div');
    tmp.innerHTML = html.trim();
    const fresh = tmp.firstElementChild;
    if (!fresh) return;
    el.replaceWith(fresh);
  });
}

function updateCollectionState(sortValue, defaultSort) {
  const wrapper = document.querySelector('[data-as-collection-state]');
  if (!wrapper) return;
  const isDefault = !sortValue || sortValue === defaultSort;
  wrapper.setAttribute('data-as-collection-state', isDefault ? 'curated' : 'filtered');
}

// MARK:- Bar update is inline (no SRA swap). Trigger label mirrors the dropdown by reading the matching radio's <label for="..."> text — same source as snippets/sort-by-dropdown.liquid's first-paint label, so the dropdown and trigger stay in lockstep without a separate name map. Radio `checked` state needs setting on popstate (browser doesn't auto-check from URL); for direct user clicks it's already correct.
function updateBarFromSort(sortValue, defaultSort, syncCheckedState) {
  const bar = document.querySelector('[data-as-collection-bar]');
  if (!bar) return;
  const isDefault = !sortValue || sortValue === defaultSort;
  const label = bar.querySelector('[data-selected]');
  if (label) {
    if (isDefault) {
      label.textContent = 'Sort';
    } else {
      const radio = bar.querySelector(`input[name="sort-by"][value="${sortValue}"]`);
      const selectedLabel = radio ? bar.querySelector(`label[for="${radio.id}"]`) : null;
      label.textContent = selectedLabel ? selectedLabel.textContent.trim() : sortValue;
    }
  }
  if (syncCheckedState) {
    bar.querySelectorAll('input[name="sort-by"]').forEach((radio) => {
      radio.checked = radio.value === sortValue;
    });
  }
}

class AsCollectionSort extends HTMLElement {
  connectedCallback() {
    AsCollectionSort.ensureBaseStyle();
    this.addEventListener('change', this.onChange);
    if (!AsCollectionSort.popstateBound) {
      window.addEventListener('popstate', () => AsCollectionSort.handlePopstate());
      AsCollectionSort.popstateBound = true;
    }
    this.setupFooterFade();
  }

  disconnectedCallback() {
    this.teardownFooterFade();
  }

  // MARK:- Footer-group fade for the mobile fixed facets. Sentinel is the section-group wrapper; falls back to the inner <footer> if the wrapper isn't found.
  setupFooterFade() {
    this.mql = window.matchMedia('(max-width: 960px)');
    this.onMqlChange = () => {
      if (this.mql.matches) this.startObservingFooter();
      else this.stopObservingFooter();
    };
    this.mql.addEventListener('change', this.onMqlChange);
    if (this.mql.matches) this.startObservingFooter();
  }

  startObservingFooter() {
    if (this.footerIo) return;
    const sentinel =
      document.querySelector('.shopify-section-group-footer-group') ||
      document.querySelector('footer.footer');
    if (!sentinel) return;
    this.footerIo = new IntersectionObserver((entries) => {
      const facets = this.querySelector('.collection-bar__facets');
      if (!facets) return;
      facets.classList.toggle('is-hidden', entries.some((e) => e.isIntersecting));
    });
    this.footerIo.observe(sentinel);
  }

  stopObservingFooter() {
    if (this.footerIo) {
      this.footerIo.disconnect();
      this.footerIo = null;
    }
    this.querySelector('.collection-bar__facets')?.classList.remove('is-hidden');
  }

  teardownFooterFade() {
    this.stopObservingFooter();
    if (this.mql && this.onMqlChange) {
      this.mql.removeEventListener('change', this.onMqlChange);
      this.mql = null;
      this.onMqlChange = null;
    }
  }

  static ensureBaseStyle() {
    if (document.querySelector('style[data-as-collection-sort-base]')) return;
    const style = document.createElement('style');
    style.setAttribute('data-as-collection-sort-base', '');
    style.textContent = 'as-collection-sort { display: block; }';
    document.head.appendChild(style);
  }

  static async handlePopstate() {
    const url = new URL(window.location.href);
    const sortValue = url.searchParams.get('sort_by') || '';
    const defaultSort = AsCollectionSort.readDefaultFromDOM();
    updateCollectionState(sortValue, defaultSort);
    updateBarFromSort(sortValue, defaultSort, true);
    await repaintForSort(sortValue);
  }

  static readDefaultFromDOM() {
    // Look up the bar's data-default-sort if present; otherwise '' (treat any
    // sort as "filtered"). The bar can carry a data attribute later if needed.
    const bar = document.querySelector('[data-as-collection-bar]');
    return bar?.dataset?.asDefaultSort || '';
  }

  onChange = async (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.name !== 'sort-by') return;
    const sortValue = target.value;
    const defaultSort = AsCollectionSort.readDefaultFromDOM();

    const url = buildUrl(sortValue);
    history.pushState({}, '', url.toString());
    updateCollectionState(sortValue, defaultSort);
    updateBarFromSort(sortValue, defaultSort, false);
    await repaintForSort(sortValue);
  };
}

if (!customElements.get('as-collection-sort')) {
  customElements.define('as-collection-sort', AsCollectionSort);
}
